import React, { useState, useEffect, useCallback } from 'react';
import ShieldMeter from '../components/ShieldMeter';
import StatCard from '../components/StatCard';
import TimelineChart from '../components/TimelineChart';
import DispositionChart from '../components/DispositionChart';
import AlignmentChart from '../components/AlignmentChart';
import SourceTable from '../components/SourceTable';
import SourceMap from '../components/SourceMap';
import TrendChart, { PolicyReadinessBanner } from '../components/TrendChart';
import SenderTable from '../components/SenderTable';
import DomainCards from '../components/DomainCards';
import ReportsList from '../components/ReportsList';

const API = process.env.REACT_APP_API_URL || '';

const s = {
  layout: { display:'flex', minHeight:'100vh', background:'#0F1629' },
  sidebar: {
    width:'240px', flexShrink:0,
    background:'rgba(30,45,79,0.5)',
    borderRight:'1px solid rgba(0,194,168,0.1)',
    display:'flex', flexDirection:'column',
    padding:'24px 0',
    position:'sticky', top:0, height:'100vh',
  },
  logo: {
    display:'flex', alignItems:'center', gap:'10px',
    padding:'0 20px 28px',
    borderBottom:'1px solid rgba(0,194,168,0.1)',
    marginBottom:'16px',
  },
  logoText: { fontFamily:'Space Grotesk', fontWeight:700, fontSize:'18px', color:'#F0F4FF' },
  nav: { flex:1, padding:'0 12px', display:'flex', flexDirection:'column', gap:'2px' },
  navItem: (active) => ({
    display:'flex', alignItems:'center', gap:'10px',
    padding:'10px 12px', borderRadius:'8px', cursor:'pointer',
    background: active ? 'rgba(0,194,168,0.12)' : 'transparent',
    color: active ? '#00C2A8' : '#5B7BA8',
    fontFamily:'Space Grotesk', fontWeight:500, fontSize:'14px',
    border:'none', width:'100%', textAlign:'left',
    transition:'all 0.15s',
  }),
  userBar: {
    padding:'16px 20px',
    borderTop:'1px solid rgba(0,194,168,0.1)',
    display:'flex', alignItems:'center', gap:'10px',
  },
  avatar: { width:'32px', height:'32px', borderRadius:'50%', border:'2px solid #00C2A8' },
  userName: { fontFamily:'Inter', fontSize:'13px', color:'#7B9CC8', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  main: { flex:1, overflow:'auto', padding:'32px' },
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'32px' },
  pageTitle: { fontFamily:'Space Grotesk', fontSize:'24px', fontWeight:700, color:'#F0F4FF' },
  refreshBtn: {
    display:'flex', alignItems:'center', gap:'8px',
    padding:'8px 16px', borderRadius:'8px',
    background:'rgba(0,194,168,0.1)', border:'1px solid rgba(0,194,168,0.3)',
    color:'#00C2A8', fontFamily:'Space Grotesk', fontSize:'13px', fontWeight:500,
    cursor:'pointer', transition:'all 0.15s',
  },
  grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' },
  grid3: { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'20px', marginBottom:'20px' },
  grid4: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'20px', marginBottom:'20px' },
  full: { marginBottom:'20px' },
  loading: { display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:'16px' },
  spinner: { width:'40px', height:'40px', border:'3px solid #1E2D4F', borderTopColor:'#00C2A8', borderRadius:'50%', animation:'spin 0.8s linear infinite' },
  emptyState: {
    textAlign:'center', padding:'80px 40px',
    background:'rgba(30,45,79,0.3)', borderRadius:'16px',
    border:'1px solid rgba(0,194,168,0.1)',
  },
};

const NAV = [
  { id:'overview', label:'Overview', icon:'◈' },
  { id:'trends', label:'Trends', icon:'↗' },
  { id:'domains', label:'Domains', icon:'⬡' },
  { id:'sources', label:'Source IPs', icon:'◎' },
  { id:'senders', label:'Senders', icon:'◷' },
  { id:'reports', label:'Raw Reports', icon:'≡' },
];

export default function DashboardPage({ user, setUser }) {
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [geo, setGeo] = useState({});

  const loadData = useCallback(async (bust = false) => {
    if (bust) {
      setRefreshing(true);
      await fetch(`${API}/api/reports/refresh`, { method:'POST', credentials:'include' });
    }
    try {
      const res = await fetch(`${API}/api/reports/aggregate`, { credentials:'include' });
      const json = await res.json();
      setData(json);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (tab !== 'sources' || !data?.topSources?.length) return;
    const ips = data.topSources.map(s => s.ip).join(',');
    fetch(`${API}/api/reports/geo?ips=${encodeURIComponent(ips)}`, { credentials: 'include' })
      .then(r => r.json())
      .then(setGeo)
      .catch(console.error);
  }, [tab, data]);

  async function handleLogout() {
    await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
  }

  function renderContent() {
    if (loading) return (
      <div style={s.loading}>
        <div style={s.spinner} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <span style={{ color:'#5B7BA8', fontFamily:'Space Grotesk', fontSize:'14px' }}>
          Scanning inbox for DMARC reports…
        </span>
      </div>
    );

    if (!data || data.totalReports === 0) return (
      <div style={s.emptyState}>
        <div style={{ fontSize:'48px', marginBottom:'16px' }}>📭</div>
        <h2 style={{ fontFamily:'Space Grotesk', fontSize:'20px', color:'#F0F4FF', marginBottom:'8px' }}>
          No DMARC reports found
        </h2>
        <p style={{ color:'#5B7BA8', fontFamily:'Inter', fontSize:'14px', lineHeight:1.6 }}>
          DMARC aggregate reports are sent by receiving mail servers to the address<br/>
          specified in your DNS <code style={{ color:'#00C2A8', fontFamily:'JetBrains Mono', fontSize:'12px' }}>rua=</code> tag.
          Make sure this Gmail account is set as the report recipient.
        </p>
      </div>
    );

    if (tab === 'overview') return renderOverview();
    if (tab === 'trends') return renderTrends();
    if (tab === 'domains') return <DomainCards domains={data.domains} />;
    if (tab === 'sources') return (
      <>
        <SourceMap sources={data.topSources} geo={geo} />
        <SourceTable sources={data.topSources} geo={geo} />
      </>
    );
    if (tab === 'senders') return <SenderTable senders={data.senders} />;
    if (tab === 'reports') return <ReportsList reports={data.reports} />;
  }

  function renderOverview() {
    const { passRate, dkimPassRate, spfPassRate, totalMessages, totalReports, dispositions, timeline, topSources, senders } = data;

    return (
      <>
        {/* Hero: Shield + Stats row */}
        <div style={{ display:'flex', gap:'20px', marginBottom:'20px', alignItems:'stretch' }}>
          <div style={{ background:'rgba(30,45,79,0.5)', borderRadius:'16px', border:'1px solid rgba(0,194,168,0.15)', padding:'28px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minWidth:'220px' }}>
            <ShieldMeter passRate={passRate} />
          </div>
          <div style={{ flex:1, display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
            <StatCard label="Total Emails Analyzed" value={totalMessages.toLocaleString()} sub={`from ${totalReports} reports`} color="#00C2A8" icon="✉" />
            <StatCard label="DMARC Pass Rate" value={`${passRate}%`} sub="SPF or DKIM aligned" color={passRate >= 90 ? '#00C2A8' : passRate >= 70 ? '#F5A623' : '#E8415A'} icon="✓" />
            <StatCard label="DKIM Alignment" value={`${dkimPassRate}%`} sub="DKIM pass rate" color={dkimPassRate >= 90 ? '#00C2A8' : '#F5A623'} icon="⚿" />
            <StatCard label="SPF Alignment" value={`${spfPassRate}%`} sub="SPF pass rate" color={spfPassRate >= 90 ? '#00C2A8' : '#F5A623'} icon="⊙" />
          </div>
        </div>

        {/* Timeline */}
        <div style={s.full}>
          <TimelineChart data={timeline} />
        </div>

        {/* Disposition + Alignment charts */}
        <div style={s.grid2}>
          <DispositionChart dispositions={dispositions} />
          <AlignmentChart dkimRate={dkimPassRate} spfRate={spfPassRate} passRate={passRate} />
        </div>

        {/* Top sources preview */}
        <SourceTable sources={topSources.slice(0, 5)} compact />
      </>
    );
  }

  function renderTrends() {
    const { timeline, passRate, dkimPassRate, spfPassRate, totalMessages } = data;
    return (
      <>
        <PolicyReadinessBanner timeline={timeline} />
        <TrendChart data={timeline} />

        {/* DKIM / SPF summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          {[
            { label: 'Overall Pass Rate', value: `${passRate}%`, sub: 'DKIM or SPF aligned', color: passRate >= 90 ? '#00C2A8' : passRate >= 70 ? '#F5A623' : '#E8415A' },
            { label: 'DKIM Alignment', value: `${dkimPassRate}%`, sub: 'DomainKeys Identified Mail', color: dkimPassRate >= 90 ? '#00C2A8' : '#F5A623' },
            { label: 'SPF Alignment', value: `${spfPassRate}%`, sub: 'Sender Policy Framework', color: spfPassRate >= 90 ? '#00C2A8' : '#F5A623' },
          ].map(s => (
            <div key={s.label} style={{ background: 'rgba(30,45,79,0.5)', borderRadius: '12px', border: `1px solid ${s.color}25`, padding: '20px 24px' }}>
              <div style={{ fontFamily: 'Space Grotesk', fontSize: '28px', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontFamily: 'Space Grotesk', fontSize: '13px', fontWeight: 600, color: '#F0F4FF', marginTop: '6px' }}>{s.label}</div>
              <div style={{ fontFamily: 'Inter', fontSize: '11px', color: '#5B7BA8', marginTop: '2px' }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Alignment explanation */}
        <div style={{ background: 'rgba(30,45,79,0.5)', borderRadius: '16px', border: '1px solid rgba(0,194,168,0.1)', padding: '24px' }}>
          <div style={{ fontFamily: 'Space Grotesk', fontWeight: 600, fontSize: '15px', color: '#F0F4FF', marginBottom: '16px' }}>
            How alignment works
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {[
              {
                title: 'DKIM alignment',
                icon: '⚿',
                body: 'The email is cryptographically signed by the sending server. DMARC checks that the signing domain matches the From: header domain. Best protection — signature travels with the email.',
              },
              {
                title: 'SPF alignment',
                icon: '⊙',
                body: 'The sending IP must be listed in your DNS SPF record, and the envelope sender domain must match the From: header. Can break when email is forwarded.',
              },
              {
                title: 'DMARC pass rule',
                icon: '✓',
                body: 'An email passes DMARC if either DKIM or SPF aligns with the From: header domain. Both passing is ideal; one is sufficient.',
              },
              {
                title: 'Relaxed vs strict alignment',
                icon: '≈',
                body: 'Relaxed (default): subdomain match is allowed, e.g. mail.example.com satisfies example.com. Strict: exact domain match required. Shown as adkim= and aspf= on the Domains tab.',
              },
            ].map(item => (
              <div key={item.title} style={{ padding: '14px 16px', borderRadius: '8px', background: 'rgba(15,22,41,0.4)', border: '1px solid rgba(91,123,168,0.15)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px', color: '#00C2A8' }}>{item.icon}</span>
                  <span style={{ fontFamily: 'Space Grotesk', fontSize: '13px', fontWeight: 600, color: '#F0F4FF' }}>{item.title}</span>
                </div>
                <div style={{ fontFamily: 'Inter', fontSize: '12px', color: '#5B7BA8', lineHeight: 1.6 }}>{item.body}</div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  const tabLabels = { overview:'Overview', trends:'Trends', domains:'Domains', sources:'Source IPs', senders:'Senders', reports:'Raw Reports' };

  return (
    <div style={s.layout}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Sidebar */}
      <aside style={s.sidebar}>
        <div style={s.logo}>
          <svg width="28" height="28" viewBox="0 0 96 96" fill="none">
            <path d="M48 8L16 22V46C16 63.2 30 79.1 48 84C66 79.1 80 63.2 80 46V22L48 8Z" fill="rgba(0,194,168,0.15)" stroke="#00C2A8" strokeWidth="2"/>
            <path d="M36 48L44 56L60 40" stroke="#00C2A8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={s.logoText}>DMARC Shield</span>
        </div>

        <nav style={s.nav}>
          {NAV.map(item => (
            <button key={item.id} style={s.navItem(tab === item.id)} onClick={() => setTab(item.id)}>
              <span style={{ fontSize:'16px', width:'20px', textAlign:'center' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <div style={s.userBar}>
          {user?.picture
            ? <img src={user.picture} alt="" style={s.avatar} referrerPolicy="no-referrer" />
            : <div style={{ ...s.avatar, background:'#1E2D4F', display:'flex', alignItems:'center', justifyContent:'center', color:'#00C2A8', fontSize:'14px' }}>
                {user?.email?.[0]?.toUpperCase()}
              </div>
          }
          <span style={s.userName}>{user?.email}</span>
          <button onClick={handleLogout} title="Sign out" style={{ background:'none', border:'none', cursor:'pointer', color:'#5B7BA8', fontSize:'16px', padding:'4px' }}>⏻</button>
        </div>

      </aside>

      {/* Main content */}
      <main style={s.main}>
        <div style={s.header}>
          <div>
            <div style={{ fontFamily:'Space Grotesk', fontSize:'12px', color:'#5B7BA8', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'4px' }}>
              Email Authentication Monitor
            </div>
            <h1 style={s.pageTitle}>{tabLabels[tab]}</h1>
          </div>
          <button
            style={{ ...s.refreshBtn, opacity: refreshing ? 0.6 : 1 }}
            onClick={() => loadData(true)}
            disabled={refreshing}
          >
            <span style={{ display:'inline-block', animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}>↻</span>
            {refreshing ? 'Refreshing…' : 'Refresh Data'}
          </button>
        </div>

        {renderContent()}
      </main>
    </div>
  );
}
